import { useMemo, useState } from 'react';
import {
  Alert,
  Collapse,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  List,
  ListItem,
  Stack,
  Typography,
} from '@mui/material';
import { ContainedButton, Dialog, TextButton } from '@shared/components';
import { BodyLoader } from '@retail/components';
import { SpreadSheetRow } from '@retail/products/types';
import {
  ItemIdentifierKey,
  ItemIdentifiers,
  ItemIdentifiersFilter,
  matchItemIdentifiers,
  parseItemIdentifiersSpreadsheet,
} from '@retail/products/utils';
import { useProductsTFunction } from '@retail/products/i18n';
import { UploadContainer } from './UploadContainer';

interface Props {
  /** The items the uploaded identifiers are matched against */
  items: ItemIdentifiers[] | undefined;
  isLoadingItems: boolean;
  onApply: (filter: ItemIdentifiersFilter) => void;
  onClose: () => void;
}

export function ItemIdentifiersFilterDialog({ items, isLoadingItems, onApply, onClose }: Props) {
  const t = useProductsTFunction();
  const [importedFile, setImportedFile] = useState<File>();
  const [identifiers, setIdentifiers] = useState<ItemIdentifiers[]>();

  const labels: Record<ItemIdentifierKey, string> = {
    mgItemNumber: t('products.itemIdentifiersFilter.mgItemNumber'),
    finfoNumber: t('products.itemIdentifiersFilter.finfoNumber'),
    nobbNumber: t('products.itemIdentifiersFilter.nobbNumber'),
  };

  const match = useMemo(
    () => (identifiers && items ? matchItemIdentifiers(identifiers, items) : undefined),
    [identifiers, items]
  );
  const totalCount = match ? match.found.length + match.notFound.length : 0;

  const onUpload = (rows: SpreadSheetRow[], file: File) => {
    setImportedFile(file);
    setIdentifiers(parseItemIdentifiersSpreadsheet(rows));
  };

  const onRemoveFile = () => {
    setImportedFile(undefined);
    setIdentifiers(undefined);
  };

  const handleApply = () => {
    if (!importedFile || !match) return;
    onApply({ fileName: importedFile.name, identifiers: match.found });
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('products.itemIdentifiersFilter.title')}</DialogTitle>

      <DialogContent>
        <Stack gap={2}>
          <Typography variant="body2">{t('products.itemIdentifiersFilter.description')}</Typography>
          <UploadContainer
            importedFile={importedFile}
            importSpreadsheet={onUpload}
            resetImportedStates={onRemoveFile}
          />

          {importedFile && !identifiers && (
            <Alert severity="error">
              {t('products.itemIdentifiersFilter.noRecognizedColumns')}
            </Alert>
          )}
          {identifiers && isLoadingItems && <BodyLoader />}
          {match && totalCount === 0 && (
            <Alert severity="warning">{t('products.itemIdentifiersFilter.noRows')}</Alert>
          )}
          {match && totalCount > 0 && (
            <Stack gap={1}>
              <Alert severity="success">
                {t('products.itemIdentifiersFilter.found', {
                  count: match.found.length,
                  totalCount,
                })}
              </Alert>
              {match.notFound.length > 0 && (
                <>
                  <Alert severity="warning">
                    {t('products.itemIdentifiersFilter.notFound', {
                      count: match.notFound.length,
                      totalCount,
                    })}
                  </Alert>
                  <NotFoundItemIdentifiers notFound={match.notFound} labels={labels} />
                </>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <TextButton onClick={onClose} size="small">
          {t('products.itemIdentifiersFilter.cancel')}
        </TextButton>
        <ContainedButton onClick={handleApply} disabled={!match?.found.length} size="small">
          {t('products.itemIdentifiersFilter.apply')}
        </ContainedButton>
      </DialogActions>
    </Dialog>
  );
}

interface NotFoundProps {
  notFound: ItemIdentifiers[];
  labels: Record<ItemIdentifierKey, string>;
}

function NotFoundItemIdentifiers({ notFound, labels }: NotFoundProps) {
  const t = useProductsTFunction();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Link
        component="button"
        variant="body2"
        alignSelf="flex-start"
        onClick={() => setIsOpen((open) => !open)}
      >
        {isOpen
          ? t('products.itemIdentifiersFilter.hideNotFound')
          : t('products.itemIdentifiersFilter.showNotFound')}
      </Link>
      <Collapse in={isOpen}>
        <List dense disablePadding sx={{ maxHeight: 240, overflow: 'auto' }}>
          {notFound.map((ids, index) => (
            <ListItem key={index} disableGutters>
              <Typography variant="body2">
                {Object.entries(ids)
                  .map(([key, value]) => `${labels[key as ItemIdentifierKey]}: ${value}`)
                  .join(' · ')}
              </Typography>
            </ListItem>
          ))}
        </List>
      </Collapse>
    </>
  );
}
